import { Injectable } from '@angular/core';

import { Observable } from 'rxjs/internal/Observable';
import { Subject } from 'rxjs/internal/Subject';

import { OpenAiService } from '@app/services';

// A single editable surface the proofreader can read from and write corrections back to.
// `kind` drives both how we read/write text and whether the content is HTML.
export interface ProofTarget {
  kind: 'input' | 'textarea' | 'contenteditable' | 'ckeditor';
  el: HTMLElement;          // Element used to anchor the floating badge.
  isHtml: boolean;          // True for CKEditor / contenteditable (preserve markup).
  getText: () => string;
  setText: (corrected: string) => void;
}

@Injectable({ providedIn: 'root' })
export class ProofreaderService {
  // CKEditor lives inside an iframe, so its focus never reaches document:focusin in the host
  // page. We hook CKEditor's own focus event globally and push a target through this stream.
  private _ckeditorFocus$ = new Subject<ProofTarget>();
  ckeditorFocus$ = this._ckeditorFocus$.asObservable();

  private ckeditorHooked = false;

  // Input types that are not free-form prose — never offer proofreading on these.
  private readonly excludedInputTypes = new Set([
    'password', 'email', 'number', 'search', 'tel', 'url', 'date', 'time',
    'datetime-local', 'month', 'week', 'color', 'range', 'checkbox', 'radio', 'file', 'hidden'
  ]);

  constructor(private _openAiService: OpenAiService) {}


  proofread(text: string, mode: string = 'grammar'): Observable<any> {
    return this._openAiService.proofread(text, mode);
  }


  // Builds a ProofTarget from a focused DOM element, or null if it isn't an eligible surface.
  buildTargetFromElement(el: HTMLElement | null): ProofTarget | null {
    if (!el) return null;
    if (el.hasAttribute('data-no-proofread') || el.closest('[data-no-proofread]')) return null;

    const tag = el.tagName.toLowerCase();

    if (tag === 'textarea') {
      const ta = el as HTMLTextAreaElement;
      if (ta.readOnly || ta.disabled) return null;
      return {
        kind: 'textarea',
        el,
        isHtml: false,
        getText: () => ta.value,
        setText: (corrected) => this.writeNativeValue(ta, corrected)
      };
    }

    if (tag === 'input') {
      const input = el as HTMLInputElement;
      const type = (input.getAttribute('type') || 'text').toLowerCase();
      if (this.excludedInputTypes.has(type)) return null;
      if (input.readOnly || input.disabled) return null;
      const maxLen = input.maxLength;
      if (maxLen > 0 && maxLen < 15) return null; // Tiny fields (codes, etc.) aren't prose.
      return {
        kind: 'input',
        el,
        isHtml: false,
        getText: () => input.value,
        setText: (corrected) => this.writeNativeValue(input, corrected)
      };
    }

    // Plain contenteditable (not a CKEditor body — those are handled via the CKEditor hook).
    const editable = el.closest('[contenteditable=""],[contenteditable="true"]') as HTMLElement | null;
    if (editable && !editable.classList.contains('cke_editable')) {
      return {
        kind: 'contenteditable',
        el: editable,
        isHtml: true,
        getText: () => editable.innerHTML,
        setText: (corrected) => {
          editable.innerHTML = corrected;
          editable.dispatchEvent(new Event('input', { bubbles: true }));
        }
      };
    }

    return null;
  }


  // Writes a value to a native input/textarea so Angular's ngModel / form control picks it up.
  private writeNativeValue(el: HTMLInputElement | HTMLTextAreaElement, value: string): void {
    el.value = value;
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }


  // Installs a one-time global hook so every CKEditor instance: (a) re-enables native
  // spell-check (red squiggles) inside its iframe, and (b) reports focus to the overlay.
  installCkeditorHook(): void {
    if (this.ckeditorHooked) return;

    let attempts = 0;
    const tryHook = () => {
      const CKEDITOR = (window as any).CKEDITOR;
      if (!CKEDITOR) {
        if (attempts++ < 40) setTimeout(tryHook, 500); // CKEditor loads async from CDN.
        return;
      }

      this.ckeditorHooked = true;

      const wire = (editor: any) => {
        try {
          // Layer 1: turn native browser spell-check back on inside the editor iframe.
          const body = editor.document && editor.document.getBody && editor.document.getBody();
          if (body && body.$) body.$.setAttribute('spellcheck', 'true');

          // Layer 2: report focus so the overlay can anchor a badge to this editor.
          editor.on('focus', () => this._ckeditorFocus$.next(this.buildCkeditorTarget(editor)));
        } catch (_e) { /* defensive: never let a single editor break the hook */ }
      };

      CKEDITOR.on('instanceReady', (evt: any) => wire(evt.editor));
      // Catch editors that became ready before the hook installed.
      Object.keys(CKEDITOR.instances || {}).forEach(name => wire(CKEDITOR.instances[name]));
    };

    tryHook();
  }


  private buildCkeditorTarget(editor: any): ProofTarget {
    // Anchor to the editor's content area (the iframe in classic mode), not the full container —
    // the container includes the toolbar, which would paint over a badge pinned to its corner.
    let anchor: HTMLElement = editor.container && editor.container.$;
    try {
      const frame = editor.window && editor.window.getFrame && editor.window.getFrame();
      if (frame && frame.$) anchor = frame.$;                       // classic iframe mode
      else if (editor.editable && editor.editable()) anchor = editor.editable().$; // inline mode
    } catch (_e) { /* fall back to container */ }

    return {
      kind: 'ckeditor',
      el: anchor,
      isHtml: true,
      getText: () => editor.getData(),
      setText: (corrected) => editor.setData(corrected)
    };
  }
}
